import {
    ApiClient,
    ApiException,
    CardChatExamples,
    CardChatRequest,
    CorpusSearchRequest,
    CreateMeaningTagRequest,
    MeaningKind,
    MeaningRequest,
    PinMeaningExampleRequest,
    ReplaceMeaningTagsRequest,
    SelectionRange,
    TrainingReviewRequest,
    UpdateUserSettingsRequest,
    VideoTextSearchRequest,
    type ExampleDto,
    FeedItemDto,
    type GetFeedPageResponse,
    type LiveFeedResponse,
    type GetMeaningPageResponse,
    type MeaningAlternativeDto,
    type MeaningContentDto,
    type MeaningCreationResponse as GeneratedMeaningCreationResponse,
    type MeaningDto,
    type MeaningTagDto,
    type CardNewsSearchPageResponse,
    type PhraseChipDto,
    PhraseTokenDto,
    type SupportedLanguage,
    type TagStatisticsResponse,
    type TrainingCardResponse,
    type TrainingCardStateResponse,
    type TrainingCardsResponse,
    type TrainingIntervalPreviewResponse,
    type TrainingReviewLogResponse,
    type UserAccountResponse,
    type WeeklyStatisticsResponse,
    type YoutubeCaptionItemDto,
    type YoutubeCaptionTrackResponse,
    type YoutubeCueDto,
    type YoutubeHighlightSpan as GeneratedYoutubeHighlightSpan,
    type YoutubeSearchItemDto,
    YoutubeSearchPageResponse,
} from '@/generated/api-client';
import type { User } from '@/types';
import { auth } from './auth';

// Aliases keeping page-facing names stable over the generated DTOs.
export { MeaningKind };
export type MeaningResult = MeaningDto;
export type MeaningResponse = MeaningContentDto;
export type MeaningTag = MeaningTagDto;
export type MeaningExample = ExampleDto;
export type MeaningPageResult = GetMeaningPageResponse;
export type FeedItem = FeedItemDto & { senseConfirmed?: boolean };
export type FeedPageResult = GetFeedPageResponse;
export type LiveFeedResult = LiveFeedResponse;
export type PhraseChip = PhraseChipDto;
export type { PhraseTokenDto };

/**
 * A phrase-chip filter as the search endpoints read it: which chips are on, which of them gave up
 * their word, and which of them gave up their part of speech. A chip that gave up both is simply
 * left out of `words` — that is what switching it off means.
 */
export interface PhraseChipSelection {
    /** Chip lemmas that are switched on. */
    words: readonly string[];
    /** Chip lemmas whose word half is ANY: any word of that part of speech fills the slot. */
    anyWords: readonly string[];
    /** Per-chip overrides written as `lemma:UPOS`; `lemma:*` accepts any part of speech. */
    posOverrides: readonly string[];
}
export type YoutubeCaption = YoutubeCaptionItemDto;
export type YoutubeCaptionTrack = YoutubeCaptionTrackResponse;
export type YoutubeCue = YoutubeCueDto;
export type YoutubeHighlightSpan = GeneratedYoutubeHighlightSpan;
export type YoutubeSearchItem = YoutubeSearchItemDto;
export type YoutubeSearchPageResult = YoutubeSearchPageResponse;
export type TrainingCard = TrainingCardResponse;
export type TrainingCardState = TrainingCardStateResponse;
export type TrainingReviewLog = TrainingReviewLogResponse;
export type TrainingIntervalPreview = TrainingIntervalPreviewResponse;
export type TrainingRating = string;
export type WeeklyStatistics = WeeklyStatisticsResponse;
export type TagStatistics = TagStatisticsResponse;

export type CardChatExampleSet = CardChatExamples;

export type CardChatHistoryTurn = {
    userMessage: string;
    assistantMessage: string;
    examples: CardChatExampleSet[];
};

export type CardChatStreamEvent =
    | { type: 'searching' }
    | { type: 'examples'; examples: CardChatExampleSet };

// Domain refinements the spec models as plain strings.

export type MeaningCreationResponse = GeneratedMeaningCreationResponse;

export type {
    MeaningDto,
    SupportedLanguage,
    TrainingCardsResponse,
} from '@/generated/api-client';

export const BACKEND_URL = import.meta.env?.VITE_BACKEND_URL ?? 'http://localhost:5005';
export const LANDING_URL = import.meta.env?.VITE_LANDING_URL ?? 'http://localhost:4321';

export const pronunciationUrl = (cardId: string): string =>
    `${BACKEND_URL}/api/meaning/${encodeURIComponent(cardId)}/pronunciation`;

let unauthorizedListener: (() => void) | null = null;

export const setUnauthorizedListener = (listener: (() => void) | null): void => {
    unauthorizedListener = listener;
};

const notifyOnUnauthorized = {
    fetch: async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
        const response = await fetch(url, init);
        if (response.status === 401) {
            unauthorizedListener?.();
        }
        return response;
    },
};

const _client = new ApiClient(BACKEND_URL, notifyOnUnauthorized);
const _sessionClient = new ApiClient(BACKEND_URL);

const postMeaningJson = async <T>(path: string, body: unknown): Promise<T> => {
    const response = await notifyOnUnauthorized.fetch(`${BACKEND_URL}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error(`Meaning request failed (${response.status}).`);
    }

    return response.json() as Promise<T>;
};

const deleteMeaningJson = async (path: string): Promise<void> => {
    const response = await notifyOnUnauthorized.fetch(`${BACKEND_URL}${path}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });

    if (!response.ok && response.status !== 404) {
        throw new Error(`Meaning request failed (${response.status}).`);
    }
};

const unavailableChatMessage = 'The AI assistant is unavailable right now. Please try again.';

type SseFrame = {
    event: string;
    data: { text?: string; message?: string; examples?: unknown };
};

// Splits a decoded SSE byte buffer into complete "\n\n"-terminated frames, leaving any
// trailing partial frame in the buffer for the next chunk. Per the SSE spec a frame can carry
// multiple `data:` lines, which are joined with '\n' before JSON-parsing.
const parseSseFrame = (frame: string): SseFrame | null => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) {
            event = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trim());
        }
    }
    if (dataLines.length === 0) return null;

    try {
        return { event, data: JSON.parse(dataLines.join('\n')) };
    } catch {
        return null;
    }
};

/**
 * Streams the AI chat response for a meaning card as it is generated, invoking `onDelta` for
 * each text fragment. Resolves once the stream completes normally; rejects on an in-band
 * `error` event, an empty stream, or an HTTP-level failure. Bypasses the NSwag-generated client
 * since it does not support Server-Sent Events (it parses the whole response body as one JSON
 * object) — see the note on `chatWithMeaningCard` in EndpointsList.cs on the API side.
 */
const chatWithMeaningCardStream = async (
    meaningId: string,
    message: string,
    onDelta: (text: string) => void,
    signal?: AbortSignal,
    onEvent?: (event: CardChatStreamEvent) => void
): Promise<void> => {
    const response = await notifyOnUnauthorized.fetch(`${BACKEND_URL}/api/meaning/${meaningId}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(new CardChatRequest({ message })),
        signal,
    });

    if (!response.ok || !response.body) {
        throw new Error(`Meaning chat request failed (${response.status}).`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let sawText = false;

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let separatorIndex = buffer.indexOf('\n\n');
        while (separatorIndex !== -1) {
            const frame = parseSseFrame(buffer.slice(0, separatorIndex));
            buffer = buffer.slice(separatorIndex + 2);

            if (frame?.event === 'delta' && frame.data.text) {
                sawText = true;
                onDelta(frame.data.text);
            } else if (frame?.event === 'searching') {
                onEvent?.({ type: 'searching' });
            } else if (frame?.event === 'examples' && frame.data.examples) {
                sawText = true;
                onEvent?.({ type: 'examples', examples: CardChatExamples.fromJS(frame.data.examples) });
            } else if (frame?.event === 'error') {
                throw new Error(frame.data.message ?? unavailableChatMessage);
            } else if (frame?.event === 'done') {
                return;
            }

            separatorIndex = buffer.indexOf('\n\n');
        }
    }

    if (!sawText) {
        throw new Error(unavailableChatMessage);
    }
};

export interface SignInOptions {
    signup?: boolean;
    replace?: boolean;
}

export const signInWithBackend = (returnUrl?: string, options: SignInOptions = {}) => {
    auth.clearSignOutInProgress();

    const redirectUrl = typeof returnUrl === 'string'
        ? returnUrl
        : `${window.location.origin}/auth/callback`;

    const loginUrl = new URL('/auth/login', BACKEND_URL);
    loginUrl.searchParams.set('returnUrl', redirectUrl);
    if (options.signup) {
        loginUrl.searchParams.set('screenHint', 'signup');
    }

    if (options.replace) {
        window.location.replace(loginUrl.toString());
    } else {
        window.location.assign(loginUrl.toString());
    }
};

// POST /auth/logout is a cookie-clearing redirect flow, not an API call; it is
// absent from the OpenAPI document (RequestDelegate-mapped), hence the form post.
export const signOutWithBackend = () => {
    const logoutUrl = new URL('/auth/logout', BACKEND_URL);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = logoutUrl.toString();
    form.style.display = 'none';

    document.body.appendChild(form);
    form.submit();
};

const toUser = (account: UserAccountResponse): User => ({
    id: account.id,
    email: account.email ?? '',
    name: account.name ?? null,
    pictureUrl: account.pictureUrl ?? null,
    nativeLanguage: account.nativeLanguage ?? null,
    learningLanguage: account.learningLanguage ?? null,
    useSlottedForm: account.useSlottedForm ?? false,
    onboardingCompleted: account.onboardingCompleted ?? false,
});

/** One page of live news matches. */
export interface NewsLivePage {
    items: FeedItem[];
    nextCursor?: string;
    hasMore: boolean;
}

const noYoutubeMatches: YoutubeSearchPageResult = new YoutubeSearchPageResponse({
    items: [],
    nextCursor: undefined,
    hasMore: false,
});

const cardSearchRequest = (
    phrase: readonly PhraseTokenDto[],
    selection: PhraseChipSelection,
    cursor: string | null | undefined,
    limit: number
) =>
    new CorpusSearchRequest({
        phrase: phrase.map((token) => PhraseTokenDto.fromJS(token)),
        words: [...selection.words],
        anyWords: [...selection.anyWords],
        posOverrides: [...selection.posOverrides],
        limit,
        cursor: cursor ?? undefined,
    });

// A live hit is not a stored match, so it has no match id or card of its own: the sentence id is
// unique within the news corpus, and the card is the one that was searched.
const toFeedItem = (
    { example: item, senseConfirmed }: CardNewsSearchPageResponse['items'][number],
    meaningId: string,
    phraseText: string
): FeedItem =>
    Object.assign(new FeedItemDto({
        matchId: `news:${item.sentenceId}`,
        meaningId,
        phraseText,
        sentence: item.sentence,
        matchedTokens: item.highlightSpans,
        articleUrl: item.articleUrl,
        articleTitle: item.articleTitle,
        articleDomain: item.articleDomain,
        siteName: item.siteName,
        logoUrl: item.logoUrl,
        publishedAtUtc: item.publishedAtUtc,
        createdAtUtc: item.createdAtUtc,
        segmentId: item.sentenceId,
    }), { senseConfirmed });

export const apiClient = {
    // Auth
    getMe: async (): Promise<User | null> => {
        try {
            return toUser(await _sessionClient.getCurrentUser());
        } catch (error) {
            if (ApiException.isApiException(error) && (error.status === 401 || error.status === 403 || error.status === 404)) {
                return null;
            }
            throw error;
        }
    },

    getSupportedLanguages: (): Promise<SupportedLanguage[]> => _client.getSupportedLanguages(),

    // Every field is optional end to end: the endpoint applies the ones that arrive and leaves the rest
    // alone, so a caller that does not show a setting must not send it.
    updateUserSettings: (settings: {
        nativeLanguage?: string;
        learningLanguage?: string;
        useSlottedForm?: boolean;
    }): Promise<User> =>
        _client.updateUserSettings(new UpdateUserSettingsRequest(settings)).then(toUser),

    // Meanings
    getPagedMeanings: (
        cursor?: string | null,
        limit = 20,
        tagId?: string | null,
        search?: string | null,
        kind?: MeaningKind | null
    ): Promise<MeaningPageResult> =>
        _client.getMeaning(limit, cursor ?? undefined, tagId ?? undefined, search?.trim() || undefined, kind ?? undefined),

    getMeaningById: (meaningId: string): Promise<MeaningResult> =>
        _client.getMeaningById(meaningId),

    getMeaningCollocations: (meaningId: string, signal?: AbortSignal) =>
        _client.getMeaningCollocations(meaningId, signal),

    chatWithMeaningCard: chatWithMeaningCardStream,

    getCardChat: async (cardId: string): Promise<CardChatHistoryTurn[]> => {
        const response = await _client.getCardChat(cardId);
        const turns: CardChatHistoryTurn[] = [];
        let pendingUser: string | null = null;
        for (const message of response.messages ?? []) {
            if (message.role === 'user') {
                pendingUser = message.text;
            } else if (message.role === 'assistant' && pendingUser !== null) {
                turns.push({
                    userMessage: pendingUser,
                    assistantMessage: message.text,
                    examples: message.examples ?? [],
                });
                pendingUser = null;
            }
        }
        return turns;
    },

    resetCardChat: (cardId: string): Promise<void> => _client.resetCardChat(cardId),

    createMeaningTag: (name: string): Promise<MeaningTag> =>
        _client.createMeaningTag(new CreateMeaningTagRequest({ name })),

    // Not yet in the generated client (backend endpoint added without a full NSwag regeneration).
    deleteMeaningTag: (tagId: string): Promise<void> =>
        deleteMeaningJson(`/api/meaning/tags/${tagId}`),

    // Statistics
    getWeeklyStatistics: (timeZone: string): Promise<WeeklyStatistics> =>
        _client.getWeeklyStatistics(timeZone),

    getTagStatistics: (): Promise<TagStatistics> => _client.getTagStatistics(),

    // Feed
    getFeedPage: (
        cursor?: string | null,
        limit = 20,
        meaningId?: string | null,
        signal?: AbortSignal
    ): Promise<FeedPageResult> =>
        _client.getFeed(limit, cursor ?? undefined, meaningId ?? undefined, signal),

    getLiveFeed: (signal?: AbortSignal): Promise<LiveFeedResult> =>
        _client.getLiveFeed(signal),

    // Live corpus search for a card's phrase and its chip relaxations. The phrase travels with the
    // request, so the corpus needs no card lookup.
    searchNewsPage: async (
        meaningId: string,
        phraseText: string,
        phrase: readonly PhraseTokenDto[],
        selection: PhraseChipSelection,
        cursor?: string | null,
        limit = 10,
        signal?: AbortSignal,
        checkSense = false
    ): Promise<NewsLivePage> => {
        // A card with no phrase has nothing to look for.
        if (phrase.length === 0) return { items: [], hasMore: false };
        const page = await _client.searchCardNews(
            meaningId,
            checkSense,
            cardSearchRequest(phrase, selection, cursor, limit),
            signal
        );
        return {
            items: (page.items ?? []).map((item) => toFeedItem(item, meaningId, phraseText)),
            nextCursor: page.nextCursor,
            hasMore: page.hasMore,
        };
    },

    // Free-text video search: the server lemmatizes the query into a phrase of any part of speech.
    searchYoutube: (
        query: string,
        cursor?: string | null,
        limit = 20,
        signal?: AbortSignal
    ): Promise<YoutubeSearchPageResult> =>
        _client.searchCorpusVideoText(
            new VideoTextSearchRequest({ query, limit, cursor: cursor ?? undefined }),
            signal
        ),

    // Video search for a card's phrase, with the chips the reader has switched on, relaxed or off.
    searchVideos: (
        phrase: readonly PhraseTokenDto[],
        selection: PhraseChipSelection,
        cursor?: string | null,
        limit = 20,
        signal?: AbortSignal
    ): Promise<YoutubeSearchPageResult> =>
        phrase.length === 0
            ? Promise.resolve(noYoutubeMatches)
            : _client.searchCorpusVideo(cardSearchRequest(phrase, selection, cursor, limit), signal),

    getYoutubeCaptions: (videoId: string): Promise<YoutubeCaptionTrack> =>
        _client.getYoutubeCaptions(videoId),

    replaceMeaningTags: (
        meaningId: string,
        tagIds: string[],
        newTagNames: string[]
    ): Promise<MeaningTag[]> =>
        _client.replaceMeaningTags(meaningId, new ReplaceMeaningTagsRequest({ tagIds, newTagNames })),

    createMeaning: (textInput: string, selections: SelectionRange[] = []): Promise<MeaningCreationResponse> =>
        postMeaningJson<MeaningCreationResponse>('/api/meaning', {
            textInput,
            selections: selections.map((selection) => ({ start: selection.start, length: selection.length })),
        }),

    createManualMeaning: (input: { textInput: string; translation?: string; definition?: string }): Promise<MeaningCreationResponse> =>
        postMeaningJson<MeaningCreationResponse>('/api/meaning/manual', input),

    updateMeaning: (meaningId: string, text: string): Promise<MeaningResult> =>
        _client.putMeaning(meaningId, new MeaningRequest({ text })),

    regenerateMeaning: (meaningId: string): Promise<MeaningResult> =>
        _client.regenerateMeaning(meaningId),

    pinMeaningExample: async (
        meaningId: string,
        exampleIndex: number,
        pinned: boolean
    ): Promise<void> => {
        await _client.pinMeaningExample(
            meaningId,
            new PinMeaningExampleRequest({ exampleIndex, pinned }));
    },

    regenerateMeaningExamples: (meaningId: string): Promise<MeaningExample[]> =>
        _client.regenerateMeaningExamples(meaningId),

    // Idempotent: returns the stored list when the card already has alternatives.
    generateMeaningAlternatives: (meaningId: string): Promise<MeaningAlternativeDto[]> =>
        _client.generateMeaningAlternatives(meaningId),

    deleteMeaning: (meaningId: string): Promise<void> => _client.deleteMeaning(meaningId),

    // Review / training
    getTrainingCards: (limit = 20): Promise<TrainingCardsResponse> =>
        _client.getTrainingCards(limit),

    reviewTrainingCard: (cardId: string, rating: TrainingRating): Promise<void> =>
        _client.reviewTrainingCard(cardId, new TrainingReviewRequest({ rating })),
};
